// TeamRoleTree — collapsible Team -> Role -> Person tree for the
// `/admin/team-mapping` page (PRODOPSAI-509).
//
// Renamed component (not `OrgTree`) by design: `OrgTree` is the Entra
// manager hierarchy from PRODOPSAI-466 and the two views must stay
// distinct (epic decision).
//
// Hides empty role groups by relying on the backend filter:
// `ITeamMembershipTreeRoleGroup` is only present in the response when its
// `persons` list is non-empty (server-side guarantee from US3 / PR2). The
// tree therefore renders exactly the role headers it receives — no
// client-side "is this group empty?" check.

import { useMemo, useState } from 'react';
import type { ITeamMembershipTreeTeam } from '@skystone/shared';
import { cn } from '@/lib/utils';
import {
  filterTeamsByRegion,
  filterTreeBySearch,
  groupTeamsByRegion,
} from './team-role-tree-helpers';
import { TeamRoleTreeNode } from './TeamRoleTreeNode';

export interface TeamRoleTreeProps {
  teams: ITeamMembershipTreeTeam[];
  /** Page-level search query — empty string means "no filter". */
  searchQuery: string;
  /** Page-level region filter — `null` means "all regions". */
  regionFilter: string | null;
  /** When `true`, archived teams appear under a separate footer group. */
  showArchived: boolean;
  /**
   * Fires after a member row's popover/trashcan successfully PATCHes /
   * DELETEs a membership. The page refetches the tree and shows the message
   * in its status banner.
   */
  onMembershipChanged?: (message: string) => void;
  /** Opens the Add dialog pre-filled to a team (PRODOPSAI-539). */
  onAddToTeam?: (teamId: string) => void;
}

export function TeamRoleTree({
  teams,
  searchQuery,
  regionFilter,
  showArchived,
  onMembershipChanged,
  onAddToTeam,
}: Readonly<TeamRoleTreeProps>) {
  // Collapse state: any id present in this set is COLLAPSED. Initial set is
  // empty -> everything is expanded on first render (US6 acceptance:
  // "Initial expand state").
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set());

  const toggle = (id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { activeRegions, archivedTeams } = useMemo(() => {
    const regionFiltered = filterTeamsByRegion(teams, regionFilter);
    const searched = filterTreeBySearch(regionFiltered, searchQuery);
    const grouped = groupTeamsByRegion(searched);
    return {
      activeRegions: grouped.active,
      archivedTeams: grouped.archived,
    };
  }, [teams, regionFilter, searchQuery, showArchived]);

  const hasAnyActiveTeam = activeRegions.some((g) => g.teams.length > 0);
  const showArchivedSection = showArchived === true && archivedTeams.length > 0;

  if (hasAnyActiveTeam === false && showArchivedSection === false) {
    return (
      <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        No teams match the current filters.
      </p>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-role-tree">
      {activeRegions.map((group) => (
        <section key={group.regionLabel} data-testid="region-group" data-region={group.regionLabel}>
          <h3 className={cn('mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground')}>
            {group.regionLabel}
          </h3>
          <div className="space-y-2">
            {group.teams.map((entry) => (
              <TeamRoleTreeNode
                key={entry.team.id}
                entry={entry}
                collapsedIds={collapsedIds}
                onToggle={toggle}
                onMembershipChanged={onMembershipChanged}
                onAddToTeam={onAddToTeam}
              />
            ))}
          </div>
        </section>
      ))}

      {showArchivedSection && (
        <section data-testid="archived-teams-group">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Archived teams
          </h3>
          <div className="space-y-2">
            {archivedTeams.map((entry) => (
              <TeamRoleTreeNode
                key={entry.team.id}
                entry={entry}
                collapsedIds={collapsedIds}
                onToggle={toggle}
                onMembershipChanged={onMembershipChanged}
                onAddToTeam={onAddToTeam}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
